import React from 'react';
import { toast } from 'react-toastify';
import type { DraggableProvided } from 'react-beautiful-dnd';
import { Draggable } from 'react-beautiful-dnd';
import clsx from 'clsx';
import { Flex } from '@itandi/itandi-bb-ui';
import Styles from './ConstructionList.module.scss';
import { toThousandSeparator } from '~/utils/CurrencySeparator';
import { ConfirmModal } from '~/components/common/parts/modal/ConfirmModal';
import { isSuccess } from '~/utils/api/api_handler';
import { ConstructionEditModal } from '~/components/common/constructions/list/ConstructionEditModal';
import type { DealConstructionItem } from '~/models/DealConstructionItem';
import type { NskResponse } from '~/models/Api';
import { Switcher } from '~/components/common/parts/switcher/Switcher';
import type {
  DealConstruction,
  DealConstructionEdit
} from '~/models/DealConstruction';
import {
  checkConstructionAddDisabled,
  getConstructionItemName
} from '~/models/DealConstruction';
import type { Deal } from '~/models/Deal';
import { isOwnStatus } from '~/models/Status';
import type { DealEstimationHeader } from '~/models/DealEstimationHeader';
import { isEmpty } from '~/utils/StringUtil';

type Props = {
  readonly: boolean;
  deal: Deal;
  dealConstruction: DealConstruction;
  requestHeader: DealEstimationHeader | null;
  handleMutateConstructions: (() => Promise<void>) | null;
  dealConstructionItems: DealConstructionItem[];
  handleUpdate:
    | ((
        submitValue: DealConstructionEdit,
        estimationId: number | null,
        historyId: number | null
      ) => Promise<NskResponse<void>>)
    | null;
  handleDestroy:
    | ((deleteConstruction: DealConstruction) => Promise<NskResponse<void>>)
    | null;
  handleUpdateEnabled:
    | ((construction: DealConstruction) => Promise<void>)
    | null;
  handleUpdateSeq:
    | ((constructions: DealConstruction[]) => Promise<void>)
    | null;
  index: number;
  target: 'owner' | 'rentee' | null;
  showHighlight: boolean;
};

export const ConstructionListRow: React.FC<Props> = ({
  readonly,
  deal,
  dealConstruction,
  requestHeader,
  dealConstructionItems,
  handleMutateConstructions,
  handleUpdate,
  handleDestroy,
  handleUpdateEnabled,
  handleUpdateSeq,
  index,
  target,
  showHighlight
}: Props) => {
  const { point, note, type } = dealConstruction;
  const historyType = dealConstruction.type === 'DealEstimationHistory';
  const constructionAddDisabled = checkConstructionAddDisabled(
    type === 'DealBilling' ? 'billing' : 'estimation',
    requestHeader,
    deal
  );
  const [showDestroyConfirm, setShowDestroyConfirm] =
    React.useState<boolean>(false);
  const [showEdit, setShowEdit] = React.useState<boolean>(false);
  const operationTypeLabel = ((): string => {
    if (!historyType) return '';
    switch (dealConstruction.operationType) {
      case 'add':
        return '（新規追加）';
      case 'edit':
        return '（変更前）';
      case 'delete':
        return '（削除）';
      default:
        return '';
    }
  })();
  const onClickEdit = (e: React.MouseEvent): void => {
    e.preventDefault();
    setShowEdit(true);
  };
  const onClickDelete = (e: React.MouseEvent): void => {
    e.preventDefault();
    setShowDestroyConfirm(true);
  };
  const handleDeleteEstimation = async (): Promise<void> => {
    if (handleDestroy === null) {
      return;
    }
    const result = await handleDestroy(dealConstruction);
    if (isSuccess(result)) {
      await handleMutateConstructions?.();
      toast.success('見積りを削除しました');
      setShowDestroyConfirm(false);
      return;
    }
    toast.error('エラーが発生しました');
  };
  const handleChangeEnabled = async (
    e: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const newConstruction = { ...dealConstruction, enabled: e.target.checked };
    await handleUpdateEnabled?.(newConstruction);
    await handleMutateConstructions?.();
  };
  const dealEstimation = historyType && dealConstruction.dealEstimation;
  const typeDelete = historyType && dealConstruction.operationType === 'delete';
  const showBeforeEstimation =
    dealEstimation && dealConstruction.operationType === 'edit';
  if (
    isOwnStatus(deal.statusId) &&
    historyType &&
    dealConstruction.operationType === 'delete'
  )
    return null;
  return (
    <Draggable
      key={dealConstruction.id}
      draggableId={String(dealConstruction.id)}
      index={index}
      isDragDisabled={handleUpdateSeq === null || readonly}
    >
      {(provided: DraggableProvided): React.ReactElement<HTMLElement> => (
        <tr
          ref={provided.innerRef}
          className={clsx(
            Styles.ListRow,
            historyType &&
              dealConstruction.operationType !== 'copy' &&
              showHighlight &&
              Styles.History,
            typeDelete && Styles.UnEnabled,
            dealConstruction.enabled ? Styles.Enabled : Styles.UnEnabled
          )}
          style={provided.draggableProps.style}
          {...provided.draggableProps}
        >
          <td className={Styles.OrderHandle} {...provided.dragHandleProps}>
            {handleUpdateSeq !== null && !readonly && (
              <img alt="drag handle" src="/assets/DragHandle.svg" />
            )}
          </td>
          {handleUpdateEnabled !== null && (
            <td className={Styles.SwitchEnable}>
              <Switcher
                checked={dealConstruction.enabled}
                disabled={
                  readonly ||
                  !dealConstruction.constructionItem?.excludeEstimation
                }
                onChange={handleChangeEnabled}
              />
            </td>
          )}
          <td className={Styles.Estimation}>
            <div className={Styles.EstimationPointAndItem}>
              {!isEmpty(point) && <div className={Styles.Point}>{point}</div>}
              <div>{getConstructionItemName(dealConstruction)}</div>
            </div>
            {!isEmpty(note) && (
              <div
                className={clsx(
                  dealConstruction.enabled
                    ? Styles.EstimationNote
                    : Styles.UnEnabledEstimationNote,
                  typeDelete && Styles.UnEnabled
                )}
              >
                備考）{note}
              </div>
            )}
            {showHighlight && (
              <>
                <div
                  className={clsx(
                    dealConstruction.enabled
                      ? Styles.EstimationHistoryPointAndItem
                      : Styles.UnEnabledHistoryPointAndItem
                  )}
                >
                  <p
                    className={clsx(
                      dealConstruction.enabled
                        ? Styles.OperationTypeInfo
                        : Styles.UnEnabledOperationTypeInfo
                    )}
                  >
                    {operationTypeLabel}
                  </p>
                  {showBeforeEstimation && (
                    <>
                      {!isEmpty(dealEstimation.point) && (
                        <div
                          className={clsx(
                            dealConstruction.enabled
                              ? Styles.HistoryPoint
                              : Styles.UnEnabled
                          )}
                        >
                          {dealEstimation.point}
                        </div>
                      )}
                      <div>{getConstructionItemName(dealEstimation)}</div>
                    </>
                  )}
                </div>
                {showBeforeEstimation &&
                  dealEstimation &&
                  !isEmpty(dealEstimation.note) && (
                    <div className={Styles.Note}>
                      備考）{dealEstimation.note}
                    </div>
                  )}
              </>
            )}
            <div className={Styles.SPInformation}>
              <div>
                <p
                  className={clsx(
                    dealConstruction.enabled
                      ? Styles.SPEstimationUnitAmount
                      : Styles.UnEnabled,
                    typeDelete && Styles.UnEnabled
                  )}
                >
                  ¥{toThousandSeparator(dealConstruction.unitAmount)} *{' '}
                  {toThousandSeparator(dealConstruction.unit)}
                  {dealConstruction.unitName}
                </p>
              </div>
              <div
                className={clsx(
                  dealConstruction.enabled
                    ? Styles.SPEstimationChargePer
                    : Styles.UnEnabled,
                  typeDelete && Styles.UnEnabled
                )}
              >
                借主{dealConstruction.renteeChargeWithPercentage} : 貸主
                {dealConstruction.ownerChargeWithPercentage} : 管理
                {dealConstruction.companyChargeWithPercentage}
              </div>
              {!isEmpty(note) && (
                <div
                  className={clsx(
                    dealConstruction.enabled
                      ? Styles.SPEstimationNote
                      : Styles.UnEnabledEstimationNote,
                    typeDelete && Styles.UnEnabled
                  )}
                >
                  備考）{note}
                </div>
              )}
              {showHighlight && (
                <>
                  <div
                    className={clsx(
                      dealConstruction.enabled
                        ? Styles.EstimationHistoryPointAndItem
                        : Styles.UnEnabled
                    )}
                  >
                    <p
                      className={clsx(
                        dealConstruction.enabled
                          ? Styles.OperationTypeSPInfo
                          : Styles.UnEnabledOperationTypeSPInfo
                      )}
                    >
                      {operationTypeLabel}
                    </p>
                    {showBeforeEstimation && (
                      <>
                        {!isEmpty(dealEstimation.point) && (
                          <div
                            className={clsx(
                              dealConstruction.enabled
                                ? Styles.HistoryPoint
                                : Styles.UnEnabled
                            )}
                          >
                            {dealEstimation.point}
                          </div>
                        )}
                        <div>{getConstructionItemName(dealEstimation)}</div>
                      </>
                    )}
                  </div>
                  {showBeforeEstimation && showHighlight && (
                    <>
                      <div>
                        ¥{toThousandSeparator(dealEstimation.unitAmount)} *{' '}
                        {toThousandSeparator(dealEstimation.unit)}
                        {dealEstimation.unitName}
                      </div>
                      <div>
                        借主{dealEstimation.renteeChargeWithPercentage} : 貸主
                        {dealEstimation.ownerChargeWithPercentage} : 管理
                        {dealEstimation.companyChargeWithPercentage}
                      </div>
                    </>
                  )}
                  {showBeforeEstimation &&
                    dealEstimation &&
                    !isEmpty(dealEstimation.note) && (
                      <div
                        className={clsx(
                          dealConstruction.enabled
                            ? Styles.SPNote
                            : Styles.UnEnabled
                        )}
                      >
                        備考）{dealEstimation.note}
                      </div>
                    )}
                </>
              )}
            </div>
          </td>
          <td className={Styles.UnitAmount}>
            <Flex direction="column" height="100%" justifyContent="center">
              <Flex flexGrow={1}>
                <p
                  className={clsx(
                    dealConstruction.enabled
                      ? Styles.EstimationUnitAmount
                      : Styles.UnEnabled,
                    typeDelete && Styles.UnEnabled
                  )}
                >
                  ¥{toThousandSeparator(dealConstruction.unitAmount)} *{' '}
                  {dealConstruction.unit}
                  {dealConstruction.unitName}
                </p>
              </Flex>
              {showBeforeEstimation && showHighlight && (
                <Flex flexGrow={1} marginTop=".5rem">
                  ¥{toThousandSeparator(dealEstimation.unitAmount)} *{' '}
                  {dealEstimation.unit}
                  {dealEstimation.unitName}
                </Flex>
              )}
            </Flex>
          </td>
          <td className={Styles.ChargePer}>
            <Flex direction="column" height="100%" justifyContent="center">
              <Flex flexGrow={1}>
                <p
                  className={clsx(
                    dealConstruction.enabled
                      ? Styles.EstimationChargePer
                      : Styles.UnEnabled,
                    typeDelete && Styles.UnEnabled
                  )}
                >
                  借主{dealConstruction.renteeChargeWithPercentage} : 貸主
                  {dealConstruction.ownerChargeWithPercentage} : 管理
                  {dealConstruction.companyChargeWithPercentage}
                </p>
              </Flex>
              {showBeforeEstimation && showHighlight && (
                <Flex flexGrow={1} marginTop=".5rem">
                  借主
                  {dealEstimation.renteeChargeWithPercentage} : 貸主
                  {dealEstimation.ownerChargeWithPercentage} : 管理
                  {dealEstimation.companyChargeWithPercentage}
                </Flex>
              )}
            </Flex>
          </td>
          {target !== null && (
            <td className={Styles.ForAmount}>
              {target === 'rentee' && (
                <span>
                  ¥{toThousandSeparator(dealConstruction.renteeAmount)}
                </span>
              )}
              {target === 'owner' && (
                <span>
                  ¥{toThousandSeparator(dealConstruction.ownerAmount)}
                </span>
              )}
            </td>
          )}
          <td className={Styles.Amount}>
            <Flex
              alignItems="end"
              direction="column"
              height="100%"
              justifyContent="center"
            >
              <Flex flexGrow={1}>
                ¥{toThousandSeparator(dealConstruction.totalAmount)}
              </Flex>
              {showBeforeEstimation && showHighlight && (
                <Flex flexGrow={1} marginTop=".5rem">
                  <p
                    className={clsx(
                      dealConstruction.enabled
                        ? Styles.HistoryTotalAmount
                        : Styles.UnEnabled
                    )}
                  >
                    ¥{toThousandSeparator(dealEstimation.totalAmount)}
                  </p>
                </Flex>
              )}
            </Flex>
          </td>
          {!readonly && (
            <td className={Styles.Edit}>
              {handleUpdate !== null && (
                <a
                  className={clsx(
                    Styles.EditLink,
                    typeDelete ||
                      constructionAddDisabled ||
                      dealConstruction.enabled === false
                      ? Styles.Disabled
                      : Styles.Enabled
                  )}
                  href="#"
                  onClick={onClickEdit}
                >
                  編集
                </a>
              )}
              {handleDestroy !== null && (
                <a
                  className={clsx(
                    Styles.DeleteLink,
                    typeDelete ||
                      constructionAddDisabled ||
                      dealConstruction.enabled === false
                      ? Styles.Disabled
                      : Styles.Enabled
                  )}
                  href="#"
                  onClick={onClickDelete}
                >
                  削除
                </a>
              )}
              {showDestroyConfirm && (
                <ConfirmModal
                  confirmType="delete"
                  handleAccept={handleDeleteEstimation}
                  handleCancel={(): void => setShowDestroyConfirm(false)}
                  isOpen={showDestroyConfirm}
                >
                  見積りを削除してよろしいですか？
                </ConfirmModal>
              )}
              {showEdit && handleUpdate !== null && (
                <ConstructionEditModal
                  dealConstruction={dealConstruction}
                  dealConstructionItems={dealConstructionItems}
                  disabled={constructionAddDisabled}
                  handleClose={(): void => setShowEdit(false)}
                  handleMutateConstructions={handleMutateConstructions}
                  handleUpdate={handleUpdate}
                />
              )}
            </td>
          )}
        </tr>
      )}
    </Draggable>
  );
};
